import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '@hooks/requests/utils/_partner_api_client';
import { PARTNER_TEAM_PATH } from '@hooks/requests/utils/paths';

export interface EditRoleParams {
  memberId: string;
  role: string;
}

export const useEditRoleMutation = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ memberId, role }: EditRoleParams): Promise<void> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      await fetchJson<{ role: string }>(
        joinUrl(allyApi, `${PARTNER_TEAM_PATH}/${encodeURIComponent(memberId)}`),
        authHeader,
        'Failed to update the role',
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        }
      );
    },
    // FR-013/FR-021: a partial failure still assigned the new role, and a 404
    // means the row is stale either way — both outcomes need the table to
    // reflect the true state, not just a plain success, so this refetches on
    // every outcome rather than only `onSuccess`.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PARTNER_TEAM_PATH] });
    },
  });
};

export default useEditRoleMutation;
